import type { ProjectTaskOption, TimeEntryItem } from '@hygge/shared';

/**
 * The Edit dialog's project, task and Billable Hours — what it offers and what
 * a save sends. Since HW-974 the dialog moves an entry to another project and
 * task on all three surfaces that open it (My Time, a task's time log, Show
 * entries). Here rather than in the component because vitest is node-env in
 * this repo and cannot mount one. [HW-974]
 */

/**
 * How a task reads in the dialog's Task combobox — key, then title. cmdk
 * filters on this text, so the reader can search by either; keys are unique,
 * so no two options share it.
 */
export function taskChoiceLabel(task: ProjectTaskOption): string {
  return `${task.key} — ${task.title}`;
}

export interface ProjectChoice {
  id: string;
  name: string;
  color: string | null;
  /** False for the entry's own project when the picker's list does not carry it. */
  offered: boolean;
}

/**
 * The owner's active projects (`useOwnerProjects` — an entry stays on its
 * owner's projects, 2026-10-09 decision), plus the entry's own project first
 * when that list does not carry it — archived, on hold, one the owner has
 * since left, or one a project manager's list stops short of (they are
 * offered only the projects they manage). Radix draws neither a name nor the
 * placeholder for a value with no item, so without it the field would render
 * empty over a live Save — Log Time's `missingProject` precedent. [HW-905]
 * [HW-974]
 */
export function projectOptions(
  offered: ReadonlyArray<{ id: string; name: string; color: string | null }>,
  current: { id: string; name: string; color: string | null }
): ProjectChoice[] {
  const list = offered.map((p) => ({ id: p.id, name: p.name, color: p.color, offered: true }));
  if (list.some((p) => p.id === current.id)) return list;
  return [{ id: current.id, name: current.name, color: current.color, offered: false }, ...list];
}

/**
 * The chosen project's open tasks, plus `keep` — the entry's current task while
 * it is still the one selected — first when the list does not carry it: a
 * finished task, or one whose list has not arrived yet. Same reason as above:
 * the select must name what it will save.
 */
export function taskOptions(
  tasks: ProjectTaskOption[] | undefined,
  keep: ProjectTaskOption | null
): ProjectTaskOption[] {
  const list = tasks ?? [];
  if (keep && !list.some((t) => t.id === keep.id)) return [keep, ...list];
  return list;
}

/**
 * The task after the project changes: kept only when it is known to belong to
 * the new project — listed among that project's loaded tasks — and cleared
 * otherwise. A return to the stored project gives back the stored task, listed
 * or not, whatever was cleared on the way, so the round trip reads as
 * unchanged. The server refuses a move that keeps a task of the old project,
 * so the dialog never sends one. `nextProjectTaskIds` is null while that
 * project's tasks have not been loaded.
 */
export function taskAfterProjectChange(input: {
  taskId: string | null;
  nextProjectId: string;
  stored: { projectId: string; taskId: string | null };
  nextProjectTaskIds: readonly string[] | null;
}): string | null {
  const { taskId, nextProjectId, stored, nextProjectTaskIds } = input;
  if (taskId !== null && nextProjectTaskIds?.includes(taskId)) return taskId;
  if (nextProjectId === stored.projectId) return stored.taskId;
  return null;
}

/** What a save adds to the times and the note — only the fields that changed. */
export interface EditTargetChanges {
  projectId?: string;
  taskId?: string | null;
  billableDuration?: number;
}

/**
 * The project, task and Billable Hours a save sends — each only when it
 * differs from the stored row. An unchanged project or task is left out so an
 * entry stored before the server's task-in-project rule stays editable for its
 * note and hours; an untouched billable figure is left out so the server's
 * "billable follows a changed duration" rule still applies.
 *
 * One exception: a Billable box the holder touched (`billableTouched` — typed
 * in at all, even back to the stored figure) is sent whenever the save also
 * changes the duration (`durationChanges`), whatever it holds. Kept at the old
 * figure on purpose, it would otherwise be moved to the new duration by that
 * rule. With the duration unchanged a box set back to the stored figure is
 * still no change, so Save stays off for it. [HW-974]
 *
 * `billableSeconds` is what the box reads — null when it is not shown or does
 * not read.
 */
export function editTargetChanges(
  stored: Pick<TimeEntryItem, 'projectId' | 'taskId' | 'billableDuration'>,
  chosen: {
    projectId: string;
    taskId: string | null;
    billableSeconds: number | null;
    billableTouched?: boolean;
    durationChanges?: boolean;
  }
): EditTargetChanges {
  const changes: EditTargetChanges = {};
  if (chosen.projectId !== stored.projectId) changes.projectId = chosen.projectId;
  if (chosen.taskId !== stored.taskId) changes.taskId = chosen.taskId;
  const billable = chosen.billableSeconds;
  const keptOnPurpose = chosen.billableTouched === true && chosen.durationChanges === true;
  if (billable !== null && (billable !== stored.billableDuration || keptOnPurpose)) {
    changes.billableDuration = billable;
  }
  return changes;
}
